// MissionHighlights.tsx

import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import {
  HighlightsContainer,
  HighlightsHeader,
  HighlightsGrid,
  SlideshowWrapper,
  SlideshowContainer,
  Slide,
  SlideOverlay,
  SlideContent,
  ContentContainer,
} from './MissionHighlights.styles';
import HomeCard from '../../../components/common/Card/HomeCard/HomeCard';
import { FaHandsHelping, FaBook, FaUsers, FaHeartbeat } from 'react-icons/fa';
import backgroundImage from '/images/mission-bg.svg';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import educationGroupImg from '/images/empoweringCommunity.jpg';
import spreadingKnowledgeImg from '/images/spreadingEducation.png';
import compassionateCareImg from '/images/holisticCare.png';

import holdHandImg from '/images/SaturationMeasuring.jpg';
import educationImg from '/images/CollectionOfPeopleOutdoor.JPG';
import communityImg from '/images/collectionOfWomen.JPG';
import hospiceCareImg from '/images/insideAShselter.jpg';

// Slideshow Images
const slideshowImages = [
  {
    src: educationGroupImg,
    alt: 'Community Gathering',
    heading: 'Empowering Communities',
    subheading: 'Building a better tomorrow, one step at a time.',
  },
  {
    src: spreadingKnowledgeImg,
    alt: 'Education Session',
    heading: 'Spreading Knowledge',
    subheading: 'Education is the key to growth and success.',
  },
  {
    src: compassionateCareImg,
    alt: 'Healthcare Support',
    heading: 'Compassionate & Holistic Care',
    subheading:
      'Because everyone deserves comprehensive support—body, mind, and spirit.',
  },
];

// Missions
const missions = [
  {
    backgroundImage: holdHandImg,
    title: 'Compassionate Care',
    description:
      'Providing empathic, holistic support for those with serious illnesses including physical, psychosocial, and spiritual care.',
    icon: <FaHandsHelping size={40} color="#FFD700" />,
    link: '/missions#compassionate-care',
  },
  {
    backgroundImage: educationImg,
    title: 'Education',
    description: 'Offering resources and training for better understanding and growth.',
    icon: <FaBook size={40} color="#FFD700" />,
    link: '/missions#education',
  },
  {
    backgroundImage: communityImg,
    title: 'Community',
    description:
      'Fostering a supportive and inclusive environment, encouraging connection and solidarity.',
    icon: <FaUsers size={40} color="#FFD700" />,
    link: '/missions#community',
  },
  {
    backgroundImage: hospiceCareImg,
    title: 'Hospice Care',
    description:
      'Delivering quality end-of-life care services that honor dignity and comfort.',
    icon: <FaHeartbeat size={40} color="#FFD700" />,
    link: '/missions#hospice-care',
  },
];

const MissionHighlights: React.FC = () => {
  const [isCenterNeeded, setIsCenterNeeded] = useState(false);

  useEffect(() => {
    const checkGridLayout = () => {
      const gridElement = document.querySelector('.highlights-grid');
      if (!gridElement) return;

      const items = gridElement.children;
      const computedStyle = getComputedStyle(gridElement);
      const gridColumnCount = computedStyle.gridTemplateColumns.split(' ').length;

      if (items.length > gridColumnCount && items.length % gridColumnCount === 1) {
        setIsCenterNeeded(true);
      } else {
        setIsCenterNeeded(false);
      }
    };

    checkGridLayout();
    window.addEventListener('resize', checkGridLayout);
    return () => window.removeEventListener('resize', checkGridLayout);
  }, [missions]);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 4500,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    arrows: false, // Disable arrows in favor of dots
    pauseOnHover: true,
    accessibility: true,
    customPaging: (i: number) => (
      <button
        style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: '#fff',
          border: 'none',
          margin: '0 5px',
          cursor: 'pointer',
        }}
        aria-label={`Go to slide ${i + 1}`}
      />
    ),
    appendDots: (dots: any) => (
      <div
        style={{
          position: 'absolute',
          bottom: '10px',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <ul style={{ margin: 0, padding: 0, display: 'flex', listStyle: 'none' }}>
          {dots}
        </ul>
      </div>
    ),
  };

  return (
    <HighlightsContainer $backgroundImage={backgroundImage}>
      <ContentContainer>
        <HighlightsHeader>
          <h2 className="mission-heading">Our Core Missions</h2>
          <p>
            Our organization is driven by four core missions that profoundly impact individuals and communities,
            addressing practical, educational, communal, and psychosocial needs.
          </p>
        </HighlightsHeader>

        <HighlightsGrid
          className={`highlights-grid ${isCenterNeeded ? 'center-single-item' : ''}`}
        >
          {missions.map((mission, index) => (
            <HomeCard
              key={index}
              backgroundImage={mission.backgroundImage}
              title={mission.title}
              description={mission.description}
              link={mission.link}
              icon={mission.icon}
            />
          ))}
        </HighlightsGrid>

        <SlideshowWrapper>
          <SlideshowContainer>
            <Slider {...sliderSettings} aria-label="Mission Highlights Slideshow">
              {slideshowImages.map((img, index) => (
                <Slide key={index}>
                  <img src={img.src} alt={img.alt} loading="lazy" />
                  <SlideOverlay />
                  <SlideContent>
                    <h3 className="slide-heading">{img.heading}</h3>
                    <p>{img.subheading}</p>
                  </SlideContent>
                </Slide>
              ))}
            </Slider>
          </SlideshowContainer>
        </SlideshowWrapper>
      </ContentContainer>
    </HighlightsContainer>
  );
};

export default MissionHighlights;
